import React from "react"

// The Figma-style solid violet arrow, inline so it needs no asset request and matches the AI/violet theme.
// A cursor SVG MUST declare intrinsic width/height (not just a viewBox) and stay small (<=128px, ~24 ideal),
// or browsers silently ignore it and fall back to the keyword after the comma in the `cursor` value.
const PICK_ARROW_SVG =
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">` +
  `<path d="M5.5 3.21V20.79c0 .45.54.67.85.35l4.24-4.24 2.5 5.36c.14.3.5.44.8.28.29-.15.43-.5.28-.79` +
  `l-2.34-5.02h5.98c.45 0 .67-.54.35-.85L6.35 2.86c-.31-.32-.85-.1-.85.35Z" ` +
  `fill="#7c3aed" stroke="#ffffff" stroke-width="1.2" stroke-linejoin="round"/></svg>`

/**
 * Cursor URLs for pick mode. `arrow` (default) is the inline violet SVG above; `hand` is the shipped asset.
 * Pass one (or any URL) as SuggestionTargetProvider's `cursorUrl`. The overlay adds the hotspot + a keyword
 * fallback, so a browser that rejects the image still shows a usable cursor.
 */
export const PICK_CURSORS = {
  arrow: `data:image/svg+xml,${encodeURIComponent(PICK_ARROW_SVG)}`,
  hand: "/images/cursors/hand.svg",
}

/**
 * A small pulsing purple dot — the "there's a pickable field inside this section" affordance for a nav
 * button. Render it only when useSuggestionGroup(groupId) reports { pickMode: true, hasTargets: true }.
 */
export const SuggestionDot: React.FC<{ className?: string }> = ({ className = "" }) => (
  <span
    className={`relative inline-flex h-2.5 w-2.5 ${className}`}
    data-testid="suggestion-dot"
    aria-hidden
  >
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-suggestion-400 opacity-75" />
    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-suggestion-500" />
  </span>
)

/**
 * "Active pick target" styling applied as border/ring STATE ON THE INPUT itself. Merge the result into the
 * field's className when the useSuggestionTarget handle's `active` is true.
 */
export const suggestionTargetClass = (active: boolean): string =>
  active
    ? "outline-none border-suggestion-500 ring-2 ring-suggestion-500 shadow-[0_0_0_3px_rgba(168,85,247,0.25)] transition-shadow"
    : ""

/**
 * Alternative: an absolutely-positioned bold outline drawn AROUND a field, for when you don't want to touch
 * the input's own border. Wrap the field and pass `active` from the useSuggestionTarget handle.
 */
export const SuggestionHighlight: React.FC<{
  active: boolean
  children: React.ReactNode
  className?: string
}> = ({ active, children, className = "" }) => (
  <div className={`relative ${className}`}>
    {children}
    {active && (
      <span
        aria-hidden
        data-testid="suggestion-highlight"
        className="pointer-events-none absolute -inset-1 rounded-lg ring-2 ring-suggestion-500 animate-pulse"
      />
    )}
  </div>
)
